import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import capitalize from "lodash/capitalize";
import { toast } from "react-toastify";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { makeStyles, lighten, useTheme } from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Checkbox from "@material-ui/core/Checkbox";
import Link from "@material-ui/core/Link";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import HttpsIcon from "@material-ui/icons/Https";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";

import type { Theme } from "src/theme";
import LoadableButton from "src/components/LoadableButton";
import { fbq, metaPurchaseEventId } from "src/utils/meta";

import {
  checkCouponApi,
  subscriptionApi,
  getFreeTrialEligibility,
} from "src/apiService/modules/stripe";
import {
  getStripeErrorMessage,
  getStripeMessageFromString,
} from "src/utils/stripeErrors";
import { clearSubscriptionResponse, logOut } from "src/store/system/actions";
import {
  paymentMethodSelector,
  selectUser,
  userQualifyingQuestionsSelector,
  userTrialEndDateSelector,
} from "src/store/system/selector";
import { colors } from "src/theme";
import TrialEndPrompt, {
  TrialEndPromptContent,
} from "src/pages/RootDialogs/TrialEndPrompt";

const COUPON_ERROR = "Coupon code not found.";
const QUALIFYING_QUESTIONS_REQUIRED_FROM = new Date("2026-05-14");

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    padding: theme.spacing(1, 1, 0),
  },
  exit: {
    justifySelf: "start",
    color: theme.palette.primary.main,
    transform: "rotate(0.5turn)",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& > * + *": {
      marginTop: theme.spacing(1.5),
    },
  },
  couponFormControl: {
    "& > div": {
      margin: 0,
    },
    margin: theme.spacing(0, 1, 0, 0),
  },
  couponButton: {
    whiteSpace: "nowrap",
    height: "40px",
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  amount: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(1.5),
  },
  nameField: {
    margin: 0,
  },
  spaceBetween: {
    "& > * + *": {
      marginLeft: theme.spacing(1),
    },
  },
  cardElementContainer: {
    "&:hover": {
      borderColor: theme.palette.text.primary,
    },
    "&.StripeElement--focus": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      padding: "9.5px 13px",
    },
    "&.StripeElement--invalid": {
      borderColor: theme.palette.error.main,
    },
    borderRadius: 8,
    width: "100%",
    borderColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[700]
        : theme.palette.grey[400],
    borderWidth: "1px",
    borderStyle: "solid",
    padding: "10.5px 14px",
    marginBottom: theme.spacing(1.5),
  },
  icon: {
    height: "20px",
    width: "20px",
    margin: theme.spacing(0, 1),
    color: lighten(theme.palette.colors.lightGreen, 0.3),
  },
  checkboxError: {
    color: theme.palette.error.main,
  },
  actions: {
    justifyContent: "center",
  },
  submit: {
    minWidth: "75%",
    backgroundColor: theme.palette.colors.lightGreen,
    "&:hover": {
      backgroundColor: theme.palette.colors.green,
    },
    margin: theme.spacing(1.5, 0, 3.5, 0),
    fontSize: theme.typography.h6.fontSize,
  },
}));

type Plan = "basic" | "premium" | "ultimate";
const Plans: Plan[] = ["ultimate", "premium", "basic"];
type Period = "annual" | "monthly";
const PlanPrices: Record<Period, Record<Plan, number>> = {
  annual: {
    ultimate: 229.9,
    premium: 169.9,
    basic: 119.9,
  },
  monthly: {
    ultimate: 22.99,
    premium: 16.99,
    basic: 11.99,
  },
};
const EBayIntegrationPrice = {
  annual: 50,
  monthly: 5,
};

function getPrice(
  plan: Plan,
  period: Period,
  ebayIntegrationsQuantity: number
): number {
  if (plan !== "ultimate") return PlanPrices[period][plan];
  return (
    PlanPrices[period][plan] +
    (ebayIntegrationsQuantity > 1
      ? (ebayIntegrationsQuantity - 1) * EBayIntegrationPrice[period]
      : 0)
  );
}

function CheckFreeTrialEligibilityWrapper({ children }) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const result = await getFreeTrialEligibility();
      if (cancel || result.eligible) return;
      setLoading(false);
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (loading)
    return (
      <Box
        width="100%"
        height="200px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );

  return children;
}

function EnterPaymentMethodDialogContent({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme() as Theme;
  const stripe = useStripe();
  const elements = useElements();
  const cardElementStyle = {
    base: {
      fontFamily: "Open Sans, Helvetica, Arial, sans-serif",
      color: theme.palette.text.primary,
      fontSize: theme.typography.body1.fontSize as string,
      letterSpacing: "0.15008px",
    },
  };
  const [period, setPeriod] = useState<Period>("annual");
  const [selectedPlan, setPlan] = useState<Plan>("ultimate");
  const [coupon, setCoupon] = useState("");
  const [name, setName] = useState<{
    first: string;
    errorFirst?: string;
    last: string;
    errorLast?: string;
  }>({
    first: "",
    last: "",
  });
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [flatDiscount, setFlatDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [ebayIntegrationsQuantity, setEbayIntegrationsQuantity] = useState(1);
  const [termsOfService, setTermsOfService] = useState({
    value: false,
    error: false,
  });

  useEffect(() => {
    if (selectedPlan === "ultimate") setEbayIntegrationsQuantity(1);
  }, [selectedPlan]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    let cancel = false;

    if (!name.first || !name.last) {
      setName((n) => {
        const name = { ...n };
        if (!n.first) name.errorFirst = "Required";
        if (!n.last) name.errorLast = "Required";
        return name;
      });

      cancel = true;
    }

    if (!termsOfService.value) {
      setTermsOfService({ value: false, error: true });
      cancel = true;
    }

    if (cancel) return;

    dispatch(clearSubscriptionResponse());

    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setLoading(true);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      card: cardElement,
      type: "card",
    });

    if (error || !paymentMethod) {
      toast.error(getStripeErrorMessage(error));
      console.error(error);
    } else {
      try {
        const response = await subscriptionApi({
          pm_id: paymentMethod.id,
          plan: selectedPlan,
          period,
          coupon,
          firstName: name.first,
          lastName: name.last,
          ebayIntegrationsQuantity:
            selectedPlan !== "ultimate" ? 1 : ebayIntegrationsQuantity,
        });

        if (response.success) {
          toast.success("Successfully updated!");
          fbq(
            "track",
            "Purchase",
            {
              currency: "USD",
              value: getPrice(selectedPlan, period, ebayIntegrationsQuantity),
              contents: [
                {
                  id: paymentMethod.id,
                  name: selectedPlan,
                  quantity:
                    selectedPlan !== "ultimate" ? 1 : ebayIntegrationsQuantity,
                },
              ],
            },
            response.subscriptionId
              ? { eventID: metaPurchaseEventId(response.subscriptionId) }
              : undefined
          );
          onClose();
        } else {
          toast.error(
            getStripeErrorMessage({
              code: response.code,
              decline_code: response.declineCode,
              message: response.message,
            })
          );
        }
      } catch (error) {
        const message =
          (error as any)?.response?.data?.message || (error as any)?.message;
        if (message === COUPON_ERROR) {
          setCouponError(message);
        } else {
          toast.error(getStripeMessageFromString(message));
        }
      }
    }

    setLoading(false);
  };

  const checkCoupon = async (code) => {
    if (!code) return;

    setCheckingCoupon(true);
    setDiscount(0);
    setCouponError("");

    try {
      const result = await checkCouponApi(code);
      setCheckingCoupon(false);
      const percentOff = result?.data?.coupon?.percent_off;
      const amountOff = result?.data?.coupon?.amount_off;
      if (percentOff) {
        setDiscount(percentOff);
        setFlatDiscount(0);
      } else if (amountOff) {
        setFlatDiscount(amountOff);
        setDiscount(0);
      } else {
        setCouponError("Invalid coupon");
        setDiscount(0);
      }
    } catch (e) {
      console.error(e);
      setCouponError("Error checking coupon");
    }
  };

  const handleLogout = () => dispatch(logOut());

  let price = getPrice(selectedPlan, period, ebayIntegrationsQuantity);
  if (discount) {
    price =
      Math.round(PlanPrices[period][selectedPlan] * (100 - discount)) / 100;
  } else if (flatDiscount) {
    price = Math.max(price - flatDiscount / 100, 0);
  }
  const displayPrice = price.toFixed(2);

  let couponIcon: JSX.Element | undefined = undefined;
  if (discount || flatDiscount) {
    couponIcon = (
      <InputAdornment position="end">
        <CheckCircleIcon style={{ color: colors.lightGreenColor }} />
      </InputAdornment>
    );
  } else if (checkingCoupon) {
    couponIcon = (
      <InputAdornment position="end">
        <CircularProgress size={18} />
      </InputAdornment>
    );
  }

  return (
    <>
      <DialogTitle disableTypography className={classes.title}>
        <Tooltip title="Click to logout">
          <IconButton className={classes.exit} onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" component="h2" align="center">
          Confirm Subscription
        </Typography>
      </DialogTitle>
      <DialogContent>
        <form noValidate autoComplete="off" className={classes.content}>
          <ButtonGroup variant="outlined" color="primary">
            <Button
              variant={period === "monthly" ? "contained" : undefined}
              onClick={() => setPeriod("monthly")}
            >
              Monthly
            </Button>
            <Button
              variant={period === "annual" ? "contained" : undefined}
              onClick={() => setPeriod("annual")}
            >
              Annual
            </Button>
          </ButtonGroup>
          <Typography align="center" variant="caption">
            Get 2 months free when you purchase an annual subscription!
          </Typography>
          <TextField
            variant="outlined"
            fullWidth
            value={selectedPlan}
            onChange={(e) => setPlan(e.target.value as Plan)}
            label="Plan"
            margin="dense"
            disabled={loading}
            select
          >
            {Plans.map((plan) => (
              <MenuItem key={plan} value={plan}>
                {capitalize(plan)} (${PlanPrices[period][plan].toFixed(2)}/
                {period === "monthly" ? "month" : "year"})
              </MenuItem>
            ))}
          </TextField>
          {selectedPlan === "ultimate" && (
            <TextField
              disabled={loading}
              variant="outlined"
              label="Number of eBay Accounts"
              margin="dense"
              type="number"
              fullWidth
              value={ebayIntegrationsQuantity}
              onChange={(ev) => {
                const value = parseInt(ev.target.value, 10) || 1;
                setEbayIntegrationsQuantity(value <= 0 ? 1 : value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={`The Ultimate plan comes with one eBay account by default. You can increase this number to integrate additional eBay accounts for an extra ${
                        period === "monthly" ? "$5/month" : "$50/year"
                      }/account.`}
                    >
                      <InfoOutlinedIcon color="action" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Box display="flex" width="100%">
            <FormControl
              variant="outlined"
              fullWidth
              className={classes.couponFormControl}
            >
              <TextField
                required
                variant="outlined"
                label="Coupon code"
                error={!!couponError}
                helperText={couponError || undefined}
                value={coupon}
                onChange={(e) => {
                  setCoupon(e.target.value);
                  if (!e.target.value) setCouponError("");
                }}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.keyCode === 13) {
                    ev.preventDefault();
                    checkCoupon(coupon);
                  }
                }}
                margin="dense"
                disabled={loading}
                InputProps={{ endAdornment: couponIcon }}
              />
            </FormControl>
            <LoadableButton
              className={classes.couponButton}
              variant="contained"
              loading={checkingCoupon}
              disabled={!coupon || loading}
              onClick={() => checkCoupon(coupon)}
            >
              Apply Coupon
            </LoadableButton>
          </Box>
          <Typography align="center" className={classes.amount}>
            Amount to be charged today: ${displayPrice}
          </Typography>
          <Box display="flex" width="100%" className={classes.spaceBetween}>
            <TextField
              required
              className={classes.nameField}
              fullWidth
              variant="outlined"
              label="First Name"
              margin="dense"
              value={name.first}
              disabled={loading}
              onChange={(e) => {
                const first = e.target.value;
                setName(({ errorFirst, ...n }) => {
                  if (first) return { ...n, first };
                  else return { ...n, errorFirst, first: "" };
                });
              }}
              error={!!name.errorFirst}
              helperText={name.errorFirst || undefined}
            />
            <TextField
              className={classes.nameField}
              fullWidth
              variant="outlined"
              label="Last Name"
              margin="dense"
              value={name.last}
              disabled={loading}
              onChange={(e) => {
                const last = e.target.value;
                setName(({ errorLast, ...n }) => {
                  if (last) return { ...n, last };
                  else return { ...n, errorLast, last: "" };
                });
              }}
              error={!!name.errorLast}
              helperText={name.errorLast || undefined}
            />
          </Box>
          <CardElement
            className={classes.cardElementContainer}
            options={{
              style: cardElementStyle,
            }}
          />
          <Box display="flex" alignItems="center" width="100%" m={0}>
            <HttpsIcon className={classes.icon} />
            <Typography variant="body2">
              Payments are handled by our secure partner{" "}
              <Link
                href="https://docs.stripe.com/security"
                color="inherit"
                underline="always"
                target="_blank"
                rel="noopener noreferrer"
              >
                Stripe
              </Link>
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" width="100%" m={0}>
            <ThumbUpIcon className={classes.icon} />
            <Typography variant="body2">14 day money back guarantee</Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            component="label"
            m={0}
          >
            <Checkbox
              className={
                termsOfService.error ? classes.checkboxError : undefined
              }
              size="small"
              name="tos"
              color="primary"
              checked={termsOfService.value}
              onChange={(ev) => {
                setTermsOfService({ error: false, value: ev.target.checked });
              }}
              disabled={loading}
            />
            <Typography
              variant="body2"
              color={
                termsOfService.error
                  ? "error"
                  : termsOfService.value
                  ? "textPrimary"
                  : "textSecondary"
              }
            >
              I have read and agree to the{" "}
              <Link
                href="https://www.myresellergenie.com/terms-of-use"
                color="inherit"
                underline="always"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                href="https://www.myresellergenie.com/privacy-policy"
                color="inherit"
                underline="always"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <LoadableButton
          className={classes.submit}
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          loading={loading}
        >
          Subscribe
        </LoadableButton>
      </DialogActions>
    </>
  );
}

function EnterPaymentMethodDialog() {
  const user = useSelector(selectUser);
  const qualifyingQuestions = useSelector(userQualifyingQuestionsSelector);
  const requirePaymentMethod = useSelector(paymentMethodSelector);
  const trialEndDate = useSelector(userTrialEndDateSelector);
  const trialEnded = trialEndDate < new Date();
  const userCreatedAt = user?.createdAt?.toDate?.();
  const requiresQualifyingQuestionsForPaywall =
    !!userCreatedAt && userCreatedAt > QUALIFYING_QUESTIONS_REQUIRED_FROM;
  const canShowPaywall =
    !requiresQualifyingQuestionsForPaywall || !!qualifyingQuestions;
  const [state, setState] = useState<{ open: boolean; trialEnded: boolean }>({
    open: requirePaymentMethod,
    trialEnded: false,
  });
  const open =
    canShowPaywall && (requirePaymentMethod || trialEnded || state.open);

  useEffect(() => {
    setState((s) => ({ ...s, open: requirePaymentMethod }));
  }, [requirePaymentMethod]);

  useEffect(() => {
    setState((s) => ({ ...s, trialEnded }));
  }, [trialEnded]);

  const handleClose = () => {
    if (!requirePaymentMethod && !trialEnded)
      setState((s) => ({ ...s, open: false }));
  };

  return (
    <>
      <Dialog
        open={open}
        scroll="body"
        fullWidth
        onClose={handleClose}
        maxWidth={state.trialEnded ? "md" : "xs"}
      >
        <CheckFreeTrialEligibilityWrapper>
          {state.trialEnded ? (
            <TrialEndPromptContent
              hasEnded
              onSubscribe={() => {
                setState({
                  open: true,
                  trialEnded: false,
                });
              }}
            />
          ) : (
            <EnterPaymentMethodDialogContent onClose={handleClose} />
          )}
        </CheckFreeTrialEligibilityWrapper>
      </Dialog>
      <TrialEndPrompt
        open={!open}
        onSubscribe={() => setState((s) => ({ ...s, open: true }))}
      />
    </>
  );
}

export default EnterPaymentMethodDialog;
